import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Dropdown, Menu } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';

import { PATHS } from 'router/config';

import {
  TCalendarRow,
  STATUS,
  isDeletableRow,
  isDraggableRow,
  parseExpenseRef,
} from '../shared';
import { ReschedulePopover } from './ReschedulePopover';

export interface IRowActionsMenuProps {
  /** The row the actions apply to. */
  row: TCalendarRow;
  /** Open the row in its edit surface (modal / ExpenseFormPopup). */
  onEdit: (row: TCalendarRow) => void;
  /** Delete a deletable row. */
  onDelete: (row: TCalendarRow) => void;
  /** Called after a successful reschedule (refetch). */
  onMoved?: () => void;
  /** Mark an unpaid expense row as paid. */
  onMarkPaid: (row: TCalendarRow) => void;
}

/**
 * The shared «⋯» actions dropdown used by both the agenda rows (Обзор) and the
 * planning-board cards (Планирование), so the two surfaces expose one action
 * vocabulary:
 *
 *  - «Отметить оплаченным» — unpaid expense rows (same gate as the to-pay list).
 *  - «Перенести»           — movable rows (editable + sourceRef, not settled);
 *                            opens the shared ReschedulePopover.
 *  - «Открыть счёт»        — rows linked to a Fakturownia invoice mirror.
 *  - «Открыть в Fakturownia» — only when the row carries an external URL.
 *  - «Открыть расход»      — expense rows without an invoice (ExpenseFormPopup).
 *  - «Редактировать»       — editable non-expense rows (PlannedPaymentModal).
 *  - «Удалить»             — deletable rows (stored PP / one-off expense).
 */
export const RowActionsMenu = memo<IRowActionsMenuProps>(
  ({ row, onEdit, onDelete, onMoved, onMarkPaid }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [menuOpen, setMenuOpen] = useState(false);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);

    const expenseRef = useMemo(() => parseExpenseRef(row.sourceRef), [
      row.sourceRef,
    ]);

    const canMarkPaid =
      row.direction === 'expense' &&
      row.status !== STATUS.paid &&
      row.status !== STATUS.cancelled &&
      Number(row.amount ?? 0) >= 0;
    const canReschedule = isDraggableRow(row);
    const hasInvoice = !!row.invoiceId;
    const hasExternalUrl = !!row.invoiceExternalUrl;
    const isExpenseRow = !!expenseRef;
    const canOpenExpense = isExpenseRow && !hasInvoice;
    const canEdit = !!row.editable && !isExpenseRow;
    const canDelete = isDeletableRow(row);

    const openInvoice = useCallback(() => {
      if (row.invoiceId) {
        history.push(
          PATHS.financesInvoice.replace(':id', String(row.invoiceId)),
        );
      }
    }, [history, row.invoiceId]);

    const openExternal = useCallback(() => {
      if (row.invoiceExternalUrl) {
        window.open(row.invoiceExternalUrl, '_blank', 'noopener,noreferrer');
      }
    }, [row.invoiceExternalUrl]);

    const openReschedule = useCallback(() => {
      setMenuOpen(false);
      setRescheduleOpen(true);
    }, []);

    const menu = (
      <Menu>
        {canMarkPaid && (
          <Menu.Item key="markPaid" onClick={() => onMarkPaid(row)}>
            {t('pages.paymentCalendar.toPay.markPaid')}
          </Menu.Item>
        )}
        {canReschedule && (
          <Menu.Item key="reschedule" onClick={openReschedule}>
            {t('pages.paymentCalendar.reschedule.title')}
          </Menu.Item>
        )}
        {hasInvoice && (
          <Menu.Item key="openInvoice" onClick={openInvoice}>
            {t('pages.paymentCalendar.agenda.openInvoice')}
          </Menu.Item>
        )}
        {hasExternalUrl && (
          <Menu.Item key="openExternal" onClick={openExternal}>
            {t('pages.paymentCalendar.agenda.openExternal')}
          </Menu.Item>
        )}
        {canOpenExpense && (
          <Menu.Item key="openExpense" onClick={() => onEdit(row)}>
            {t('pages.paymentCalendar.agenda.openExpense')}
          </Menu.Item>
        )}
        {canEdit && (
          <Menu.Item key="edit" onClick={() => onEdit(row)}>
            {t('pages.paymentCalendar.edit')}
          </Menu.Item>
        )}
        {canDelete && (
          <Menu.Item key="delete" danger onClick={() => onDelete(row)}>
            {t('pages.paymentCalendar.delete')}
          </Menu.Item>
        )}
      </Menu>
    );

    return (
      <ReschedulePopover
        row={row}
        onMoved={onMoved}
        visible={rescheduleOpen}
        onVisibleChange={setRescheduleOpen}
      >
        <Dropdown
          overlay={menu}
          trigger={['click']}
          visible={menuOpen}
          onVisibleChange={setMenuOpen}
        >
          <Button
            size="small"
            type="text"
            icon={<EllipsisOutlined />}
            aria-label={t('pages.paymentCalendar.table.actions')}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </ReschedulePopover>
    );
  },
);
